import moment from 'moment';

export const CROP_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'almond', label: 'Almond' },
  { value: 'grape', label: 'Grape' },
  { value: 'pistachio', label: 'Pistachio' },
  { value: 'walnut', label: 'Walnut' },
];

export const SOIL_TYPE_OPTIONS = [
  'unknown',
  'sand',
  'loamy-sand',
  'sandy-loam',
  'fine-sandy-loam',
  'loam',
  'silt-loam',
  'silt',
  'sandy-clay-loam',
  'clay-loam',
  'silty-clay-loam',
  'sandy-clay',
  'silty-clay',
  'clay',
].map((value) => ({
  value,
  label: value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' '),
}));

export const IRRIGATION_TYPE_OPTIONS = [
  { value: 'micro-sprinkler', label: 'Micro Sprinkler' },
  { value: 'drip', label: 'Drip' },
  { value: 'flood', label: 'Flood' },
  { value: 'furrow', label: 'Furrow' },
];

export function roundTo(value, decimalPlaces) {
  const multiplier = 10 ** decimalPlaces;
  return Math.round((Number(value) + Number.EPSILON) * multiplier) / multiplier;
}

export function safeParseFloat(value) {
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

export function convertSpacingValue(
  value,
  fromUnit,
  toUnit,
  getUserUnits,
  decimalPlaces = 3,
) {
  if (value === undefined || value === null || value === '') return value;
  const numericValue = Number(value);
  if (fromUnit === toUnit) return roundTo(numericValue, decimalPlaces);
  if (fromUnit === 'feet' && toUnit === 'meters') {
    return roundTo(numericValue * 0.3048, decimalPlaces);
  }
  if (fromUnit === 'meters' && toUnit === 'feet') {
    return roundTo(numericValue / 0.3048, decimalPlaces);
  }
  return getUserUnits(
    numericValue,
    fromUnit,
    undefined,
    { decimalPlaces },
    toUnit,
  ).value;
}

export function convertSpacingOnUnitChange(
  value,
  nextUnit,
  getUserUnits,
  decimalPlaces = 5,
) {
  const previousUnit = nextUnit === 'feet' ? 'meters' : 'feet';
  return convertSpacingValue(
    value,
    previousUnit,
    nextUnit,
    getUserUnits,
    decimalPlaces,
  );
}

export function convertLinearSpacingToSqft(
  linearSpacing,
  rowSpacing,
  linearSpacingUnit,
  rowSpacingUnitOrGetUserUnits,
  getUserUnitsArg,
  decimalPlaces = 2,
) {
  const rowSpacingUnit = typeof rowSpacingUnitOrGetUserUnits === 'string'
    ? rowSpacingUnitOrGetUserUnits
    : linearSpacingUnit;
  const getUserUnits = typeof rowSpacingUnitOrGetUserUnits === 'function'
    ? rowSpacingUnitOrGetUserUnits
    : getUserUnitsArg;

  const linearSpacingFt = convertSpacingValue(
    linearSpacing,
    linearSpacingUnit,
    'feet',
    getUserUnits,
    decimalPlaces,
  );
  const rowSpacingFt = convertSpacingValue(
    rowSpacing,
    rowSpacingUnit,
    'feet',
    getUserUnits,
    decimalPlaces,
  );

  return Number(linearSpacingFt || 0) * Number(rowSpacingFt || 0);
}

export function buildRecommendationOnboardingSpacingPayload({
  rowSpacing,
  rowSpacingUnit,
  treeSpacing,
  treeSpacingUnit,
  emitterSpacing,
  emitterSpacingUnit,
  getUserUnits,
}) {
  const rowSpacingMeters = convertSpacingValue(
    rowSpacing,
    rowSpacingUnit,
    'meters',
    getUserUnits,
    3,
  );
  const treeSpacingMeters = convertSpacingValue(
    treeSpacing,
    treeSpacingUnit,
    'meters',
    getUserUnits,
    3,
  );

  const treeSpacingFt2 = convertLinearSpacingToSqft(
    treeSpacing,
    rowSpacing,
    treeSpacingUnit,
    rowSpacingUnit,
    getUserUnits,
    5,
  );
  const emitterSpacingFt2 = convertLinearSpacingToSqft(
    emitterSpacing,
    rowSpacing,
    emitterSpacingUnit,
    rowSpacingUnit,
    getUserUnits,
    5,
  );

  return {
    row_spacing: roundTo(rowSpacingMeters, 3),
    plant_spacing: roundTo(treeSpacingMeters, 3),
    tree_spacing_ft2: roundTo(treeSpacingFt2, 2),
    emitter_spacing_ft2: roundTo(emitterSpacingFt2, 2),
  };
}

function convertSpacing(value, fromUnit, toUnit, getUserUnits) {
  if (!Number.isFinite(Number(value)) || Number(value) <= 0) return undefined;
  return convertSpacingValue(value, fromUnit, toUnit, getUserUnits);
}

function getLinearSpacingFromArea(areaSqft, rowSpacingFeet) {
  if (!rowSpacingFeet || !Number.isFinite(Number(areaSqft)) || Number(areaSqft) <= 0) {
    return undefined;
  }
  return roundTo(Number(areaSqft) / Number(rowSpacingFeet), 3);
}

export function getCropDetailsFormValues(block, getUserUnits, spacingUnit = 'feet') {
  if (!block) {
    return {
      crop: 'none',
      soilType: 'unknown',
      rowSpacing: 0,
      rowSpacingUnit: spacingUnit,
      treeSpacing: 0,
      treeSpacingUnit: spacingUnit,
      irrigationLine: 1,
      emitterSpacing: 0,
      emitterSpacingUnit: spacingUnit,
      emitterDischarge: 0,
      schedulingEnabled: false,
    };
  }

  const rowSpacingMeters = safeParseFloat(block.row_spacing);
  const rowSpacingFeet = convertSpacing(
    rowSpacingMeters,
    'meters',
    'feet',
    getUserUnits,
  );
  const treeSpacingFeet = safeParseFloat(block.plant_spacing)
    || getLinearSpacingFromArea(block.tree_spacing_ft2, rowSpacingFeet);
  const emitterSpacingFeet = safeParseFloat(block.emitter_spacing_ft2);

  const plantedDate = block.date_planted || block.growing_season_start;
  const parsedPlantedDate = plantedDate && moment(plantedDate).isValid()
    ? moment(plantedDate)
    : null;

  return {
    blockId: block.id,
    crop: block.crop || 'none',
    soilType: block.soil_type && block.soil_type !== '0' ? block.soil_type : 'unknown',
    datePlanted: parsedPlantedDate,
    rowSpacing: convertSpacing(
      rowSpacingMeters,
      'meters',
      spacingUnit,
      getUserUnits,
    ) ?? 0,
    rowSpacingUnit: spacingUnit,
    treeSpacing: convertSpacing(
      treeSpacingFeet,
      'feet',
      spacingUnit,
      getUserUnits,
    ) ?? 0,
    treeSpacingUnit: spacingUnit,
    irrigationType: block.irrigation_type && block.irrigation_type !== '0'
      ? block.irrigation_type
      : undefined,
    irrigationLine: Number(block.irrigation_line_multiplier) || 1,
    emitterSpacing: convertSpacing(
      emitterSpacingFeet,
      'feet',
      spacingUnit,
      getUserUnits,
    ) ?? 0,
    emitterSpacingUnit: spacingUnit,
    emitterDischarge: Number(block.emitter_discharge_gph) || 0,
    schedulingEnabled: block.scheduling_enabled === true,
  };
}

export function buildCropDetailsPayload(formValues, getUserUnits) {
  const toFeet = (value, unit) => convertSpacingValue(
    value || 0,
    unit,
    'feet',
    getUserUnits,
    5,
  );
  const rowSpacingFeet = toFeet(formValues.rowSpacing, formValues.rowSpacingUnit);
  const treeSpacingFeet = toFeet(formValues.treeSpacing, formValues.treeSpacingUnit);
  const emitterSpacingFeet = toFeet(
    formValues.emitterSpacing,
    formValues.emitterSpacingUnit,
  );

  const spacingPayload = buildRecommendationOnboardingSpacingPayload({
    rowSpacing: rowSpacingFeet,
    rowSpacingUnit: 'feet',
    treeSpacing: treeSpacingFeet,
    treeSpacingUnit: 'feet',
    emitterSpacing: emitterSpacingFeet,
    emitterSpacingUnit: 'feet',
    getUserUnits,
  });

  return {
    crop: formValues.crop,
    soil_type: formValues.soilType || undefined,
    date_planted: formValues.datePlanted?.format('YYYY-MM-DD') || undefined,
    ...spacingPayload,
    plant_spacing: roundTo(treeSpacingFeet, 3),
    emitter_spacing_ft2: roundTo(emitterSpacingFeet, 2),
    irrigation_type: formValues.irrigationType,
    irrigation_line_multiplier: Number.parseInt(formValues.irrigationLine, 10) || 0,
    emitter_discharge_gph: safeParseFloat(formValues.emitterDischarge),
    scheduling_enabled: formValues.schedulingEnabled,
  };
}
